/**
 * jsPDF-based PDF generator for the customer conformity export.
 *
 * Mirrors the structure of generateProductConformityPdf in conformityPdf.ts
 * but renders a customer-level REACH Art. 33 conformity letter.
 * Calls doc.save() for a direct client-side PDF download.
 */

import { jsPDF } from "jspdf"
import {
  generateNegativeText,
  generatePositiveText,
  generateRegulatoryNotes,
} from "../../../components/conformity/conformityPdf"
import type { KundenKonformitaetHtmlData } from "../html/kunden_konformitaet"

const CANDIDATE_LIST_VERSION = "31. Aktualisierung"
const CANDIDATE_LIST_DATE = "2024-06-27"
const CANDIDATE_LIST_ENTRIES = 240

function sanitize(s: string): string {
  return (s ?? "").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60)
}

type FlatSvhcRow = {
  productName: string
  casNumber: string
  substanceName: string
  svhcGehalt: string
  locationInComponent: string
}

export function generateKundenKonformitaetPdf(
  data: KundenKonformitaetHtmlData,
): void {
  const { customer } = data
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentW = pageW - 2 * margin
  let y = margin

  const addFooter = () => {
    const total = doc.getNumberOfPages()
    for (let i = 1; i <= total; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `turnus.ai QMS · Vertraulich · Seite ${i} von ${total}`,
        margin,
        pageH - 10,
      )
      doc.text(
        `REACH-Konformitätserklärung · ${customer.customerName} · ${data.creationDate}`,
        pageW - margin,
        pageH - 10,
        { align: "right" },
      )
    }
  }

  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - 20) {
      doc.addPage()
      y = margin
    }
  }

  // --- 1. Title ---------------------------------------------------------------
  doc.setFontSize(18)
  doc.setTextColor(30)
  doc.setFont("helvetica", "bold")
  doc.text("REACH-Konformitätserklärung", margin, y)
  y += 7
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(100)
  doc.text("gemäß Verordnung (EG) Nr. 1907/2006 (REACH), Artikel 33", margin, y)
  y += 4
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 2. Meta block (two columns) --------------------------------------------
  const col1X = margin
  const col2X = margin + contentW / 2 + 5
  const metaLineH = 5

  const metaLeft: [string, string][] = [
    ["Aussteller", data.issuer || "—"],
    ["Adresse", data.issuerAddress || "—"],
    ["Erstellungsdatum", data.creationDate],
  ]
  const metaRight: [string, string][] = [
    ["Empfänger", customer.customerName],
    ["Kundennummer", customer.customerId],
    [
      "Kandidatenliste",
      `${CANDIDATE_LIST_VERSION} (${CANDIDATE_LIST_ENTRIES} Einträge, ${CANDIDATE_LIST_DATE})`,
    ],
  ]

  for (let i = 0; i < 3; i++) {
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8)
    doc.setTextColor(120)
    doc.text(metaLeft[i][0], col1X, y)
    doc.text(metaRight[i][0], col2X, y)
    y += metaLineH * 0.6
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    doc.setTextColor(50)
    // Wrap long values (candidate list version)
    const leftLines = doc.splitTextToSize(metaLeft[i][1], contentW / 2 - 5)
    const rightLines = doc.splitTextToSize(metaRight[i][1], contentW / 2 - 5)
    doc.text(leftLines, col1X, y)
    doc.text(rightLines, col2X, y)
    y += metaLineH
  }

  y += 2
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 3. Declaration text ----------------------------------------------------
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Erklärung", margin, y)
  y += 7

  const declarationText = customer.isPositive
    ? generatePositiveText(customer.customerName, customer.svhcProductCount)
    : generateNegativeText(customer.customerName, customer.totalProductCount)

  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  const declLines = doc.splitTextToSize(declarationText, contentW)
  for (const line of declLines) {
    ensureSpace(5)
    doc.text(line, margin, y)
    y += 4.5
  }
  y += 6

  // --- 4. SVHC table (positive only) ------------------------------------------
  const flatRows: FlatSvhcRow[] = customer.products.flatMap((p) =>
    p.rows.map((r) => ({
      productName: p.productName,
      casNumber: r.casNumber,
      substanceName: r.substanceName,
      svhcGehalt: r.svhcGehalt,
      locationInComponent: r.locationInComponent,
    })),
  )

  if (customer.isPositive && flatRows.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(30)
    doc.text("SVHC-Stofftabelle", margin, y)
    y += 8

    const headers = [
      "Produkt",
      "CAS-Nr.",
      "SVHC-Stoff",
      "Gehalt (%)",
      "Ort im Bauteil",
    ]
    const colWidths = [34, 28, 38, 22, 48]

    // Header row
    doc.setFillColor(45, 45, 50)
    doc.rect(margin, y - 4, contentW, 7, "F")
    doc.setFontSize(7)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(255)
    let cx = margin + 2
    for (let i = 0; i < headers.length; i++) {
      doc.text(headers[i], cx, y)
      cx += colWidths[i]
    }
    y += 5

    // Data rows
    doc.setFont("helvetica", "normal")
    doc.setFontSize(7.5)
    const lineH = 3.5

    flatRows.forEach((row, idx) => {
      const vals = [
        row.productName,
        row.casNumber,
        row.substanceName,
        row.svhcGehalt,
        row.locationInComponent,
      ]
      const cellLines = vals.map((v, i) =>
        doc.splitTextToSize(v || "", colWidths[i] - 3),
      )
      const maxLines = Math.max(...cellLines.map((l) => l.length), 1)
      const rowH = Math.max(6, maxLines * lineH + 2)

      ensureSpace(rowH + 2)
      if (idx % 2 === 0) {
        doc.setFillColor(245, 245, 248)
        doc.rect(margin, y - 3.5, contentW, rowH, "F")
      }
      doc.setTextColor(60)
      cx = margin + 2
      for (let i = 0; i < vals.length; i++) {
        let lineY = y
        for (const line of cellLines[i]) {
          doc.text(line, cx, lineY)
          lineY += lineH
        }
        cx += colWidths[i]
      }
      y += rowH
    })
    y += 6
  }

  // --- 5. Regulatory notes ----------------------------------------------------
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Regulatorische Hinweise", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)

  const noteLines = generateRegulatoryNotes()
    .split("\n")
    .filter((l) => l.trim())
  for (const noteLine of noteLines) {
    const wrapped = doc.splitTextToSize(noteLine, contentW)
    for (const wl of wrapped) {
      ensureSpace(5)
      doc.text(wl, margin, y)
      y += 4.5
    }
    y += 2
  }
  y += 6

  // --- 6. Signature -----------------------------------------------------------
  ensureSpace(30)
  doc.setDrawColor(80)
  doc.setLineWidth(0.4)
  doc.line(margin, y, margin + 72, y)
  y += 5
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text(data.signatory || "—", margin, y)
  y += 5
  doc.setFont("helvetica", "normal")
  doc.setTextColor(120)
  doc.setFontSize(9)
  doc.text(data.signatoryRole || "", margin, y)
  y += 5
  if (data.signatoryPlace || data.signatoryDate) {
    doc.text(
      `${data.signatoryPlace || ""}, ${data.signatoryDate || ""}`,
      margin,
      y,
    )
  }

  addFooter()

  doc.save(
    `REACH_Konformitaetserklaerung_${sanitize(customer.customerName || customer.customerId)}.pdf`,
  )
}
